/**
 * DB Dictionary 変更レビューページ
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DB辞書の変更差分レビューUI。pending/reviewed/allフィルタ・展開diff表示・レビュー承認操作
 */

import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { usePendingChanges, useReviewChange } from '../../hooks/useDbDictionary.js';
import { useAuth } from '../../hooks/useAuth.js';
import { ErrorState } from '../../components/feedback/ErrorState/index.js';
import { KpiTableSkeleton } from '../../components/feedback/KpiTableSkeleton/index.js';
import type { PendingChangesFilter } from '../../types/db-dictionary.js';

/** @graph-connects none */
export const Route = createFileRoute('/db-dictionary/changes')({
  component: DbDictionaryChangesPage,
});

/** @graph-connects none */
const FILTER_OPTIONS: PendingChangesFilter[] = ['pending', 'reviewed', 'all'];

/** @graph-connects none */
const CHANGE_TYPE_COLORS: Record<string, 'warning' | 'info' | 'default'> = {
  columns_changed: 'warning',
  description_regenerated: 'default',
  new_table: 'info',
};

/**
 * DB辞書変更一覧ページコンポーネント
 * @graph-connects usePendingChanges [calls] 変更一覧データ取得
 * @graph-connects useReviewChange [calls] レビュー完了ミューテーション
 */
function DbDictionaryChangesPage() {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<PendingChangesFilter>('pending');
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const { can } = useAuth();
  const canEdit = can('EDIT_DB_DICTIONARY');
  const { data, isLoading, isError, refetch } = usePendingChanges(activeFilter);
  const reviewMutation = useReviewChange();
  const changes = data?.changes ?? [];

  const toggleExpanded = (qn: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(qn)) next.delete(qn);
      else next.add(qn);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('dbDictionary.changes.title')}
          </h1>
        </div>
        <KpiTableSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('dbDictionary.changes.title')}
          </h1>
        </div>
        <ErrorState
          onRetry={() => {
            void refetch();
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ヘッダー */}
      <div className="flex flex-col gap-4 animate-content-reveal">
        <Link
          to="/db-dictionary"
          className="inline-flex items-center gap-1 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {t('dbDictionary.actions.backToSchemas')}
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">
            {t('dbDictionary.changes.title')}
          </h1>
          <p className="mt-1 text-[var(--color-text-secondary)]">
            {t('dbDictionary.changes.description')}
          </p>
        </div>
      </div>

      {/* フィルタ */}
      <div className="flex gap-2 flex-wrap animate-content-reveal">
        {FILTER_OPTIONS.map((filter) => (
          <Button
            key={filter}
            variant={activeFilter === filter ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => {
              setActiveFilter(filter);
            }}
          >
            {t(`dbDictionary.changes.filter.${filter}`)}
          </Button>
        ))}
      </div>

      {/* 変更一覧 */}
      {changes.length === 0 ? (
        <Card className="animate-content-reveal">
          <CardContent>
            <div className="text-center py-8 text-[var(--color-text-muted)]">
              {t('dbDictionary.changes.noChanges')}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3 animate-content-reveal">
          {changes.map((change) => {
            const isExpanded = expandedItems.has(change.qualifiedName);
            const schema = change.qualifiedName.split('.')[0];

            return (
              <Card key={change.qualifiedName}>
                <CardContent className="py-3">
                  {/* ヘッダー行 */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <button
                        className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
                        onClick={() => {
                          toggleExpanded(change.qualifiedName);
                        }}
                      >
                        <svg
                          className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                      <Link
                        to="/db-dictionary/$schema"
                        params={{ schema }}
                        className="font-medium text-[var(--color-text-primary)] hover:text-[var(--color-accent-primary)] transition-colors truncate"
                      >
                        {change.qualifiedName}
                      </Link>
                      <Badge variant={CHANGE_TYPE_COLORS[change.changeType] ?? 'default'} size="sm">
                        {t(`dbDictionary.changes.changeType.${change.changeType}`)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-[var(--color-text-muted)]">
                        {new Date(change.detectedAt).toLocaleDateString('ja-JP')}
                      </span>
                      {change.reviewedAt ? (
                        <Badge variant="success" size="sm">
                          {t('dbDictionary.changes.filter.reviewed')}
                        </Badge>
                      ) : canEdit ? (
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => {
                            reviewMutation.mutate(change.qualifiedName);
                          }}
                          disabled={reviewMutation.isPending}
                        >
                          {t('dbDictionary.changes.markReviewed')}
                        </Button>
                      ) : (
                        <Badge variant="warning" size="sm">
                          {t('dbDictionary.changes.filter.pending')}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* 展開: before/after diff */}
                  {isExpanded && (
                    <div className="mt-3 space-y-2 border-t border-[var(--color-border)] pt-3">
                      {change.beforeDescription && (
                        <div>
                          <p className="text-xs font-medium text-[var(--color-text-muted)] mb-1">
                            {t('dbDictionary.changes.before')}
                          </p>
                          <p className="text-sm text-[var(--color-error)] bg-[var(--color-error)]/5 rounded px-2 py-1 line-through">
                            {change.beforeDescription}
                          </p>
                        </div>
                      )}
                      {change.afterDescription && (
                        <div>
                          <p className="text-xs font-medium text-[var(--color-text-muted)] mb-1">
                            {t('dbDictionary.changes.after')}
                          </p>
                          <p className="text-sm text-[var(--color-success)] bg-[var(--color-success)]/5 rounded px-2 py-1">
                            {change.afterDescription}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
